import { Device } from "@fairfleet/geotab";
import DirectionsBusSharp from "@mui/icons-material/DirectionsBusSharp";
import PlaceIcon from "@mui/icons-material/Place";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { HtmlHTMLAttributes, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NominatimResult, useLocationSearch } from "../hooks/useLocationSearch";
import { searchDevices } from "../models/searchDevices";
import { useDebounced } from "@/hooks/useDebounced";

type SearchOption = DeviceOption | LocationOption;
interface DeviceOption {
  type: "device";
  device: Device;
}
interface LocationOption {
  type: "location";
  location: NominatimResult;
}

/** How many vehicles the list offers at once. */
const MAX_DEVICE_OPTIONS = 10;

interface Props {
  /** The vehicles the map has loaded names for; searched here instead of asking Geotab again. */
  devices: ReadonlyMap<string, Device>;
  /** Map of deviceId → [lng, lat] for current positions */
  devicePositions: globalThis.Map<string, [number, number]>;
  onDeviceSelect: (deviceId: string, lng: number, lat: number) => void;
  onLocationSelect: (lng: number, lat: number) => void;
}

export default function MapSearchBar({ devices, devicePositions, onDeviceSelect, onLocationSelect }: Props) {
  const [inputValue, setInputValue] = useState("");
  const debouncedSearch = useDebounced(inputValue, 300);
  const debouncedLocation = useDebounced(inputValue, 500);
  const { t } = useTranslation();

  const matchingDevices = useMemo(
    () => searchDevices(devices.values(), debouncedSearch, MAX_DEVICE_OPTIONS),
    [devices, debouncedSearch],
  );
  const { data: locations = [], isLoading: locationsLoading } =
    useLocationSearch(debouncedLocation);

  const options: SearchOption[] = [
    ...matchingDevices.map((device): DeviceOption => ({ type: "device", device })),
    ...locations.map((location): LocationOption => ({ type: "location", location })),
  ];

  function handleChange(_: unknown, option: SearchOption | null) {
    if (!option) return;
    if (option.type === "device") {
      const pos = devicePositions.get(option.device.id);
      if (pos) {
        onDeviceSelect(option.device.id, pos[0], pos[1]);
      }
    } else {
      onLocationSelect(parseFloat(option.location.lon), parseFloat(option.location.lat));
    }
    setInputValue("");
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: "absolute",
        top: 12,
        left: 12,
        zIndex: 1000,
        width: 340,
        maxWidth: "calc(100% - 80px)",
        pointerEvents: "auto",
      }}
    >
      <Autocomplete<SearchOption>
        size="small"
        fullWidth
        freeSolo={false}
        value={null}
        loading={locationsLoading}
        options={options}
        groupBy={option => (option.type === "device" ? t("map.devices") : t("map.locations"))}
        getOptionLabel={option => {
          if (option.type === "device") return option.device.name;
          return option.location.display_name;
        }}
        getOptionKey={option => {
          if (option.type === "device") return `d-${option.device.id}`;
          return `l-${option.location.lat}-${option.location.lon}`;
        }}
        filterOptions={o => o}
        onChange={handleChange}
        onInputChange={(_, value) => setInputValue(value)}
        inputValue={inputValue}
        renderOption={renderOption}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={t("map.searchPlaceholder")}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
        slotProps={{
          paper: { elevation: 4 },
        }}
      />
    </Paper>
  );
}

function renderOption(props: HtmlHTMLAttributes<HTMLLIElement>, option: SearchOption) {
  const key = option.type === "device" ? `d-${option.device.id}` : `l-${option.location.lat}`;
  return (
    <li {...props} key={key}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden" }}>
        {option.type === "device" ? (
          <DirectionsBusSharp fontSize="small" color="primary" />
        ) : (
          <PlaceIcon fontSize="small" color="error" />
        )}
        <Typography variant="body2" noWrap>
          {option.type === "device" ? option.device.name : option.location.display_name}
        </Typography>
      </Box>
    </li>
  );
}
